import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Subscription {
    id: string;
    tenantId: string;
    planName: string;
    customerId: string;
    amount: number;
    billingCycle: string;
    status: string;
    nextBillingDate?: string;
    createdAt: string;
    updatedAt: string;
}

interface BillingState {
    subscriptions: Subscription[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchSubscriptions: (filters?: any) => Promise<void>;
    createSubscription: (data: Partial<Subscription>) => Promise<void>;
    updateSubscription: (id: string, data: Partial<Subscription>) => Promise<void>;
}

export const useBillingStore = create<BillingState>((set, get) => ({
    subscriptions: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchSubscriptions: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/subscriptions', { params: filters });
            const { data, pagination } = response.data;
            set({ subscriptions: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch subscriptions', isLoading: false });
        }
    },
    createSubscription: async (data: Partial<Subscription>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/subscriptions', data);
            const newSub = response.data;
            set((state) => ({
                subscriptions: [newSub, ...state.subscriptions],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create subscription', isLoading: false });
            throw error;
        }
    },
    updateSubscription: async (id: string, data: Partial<Subscription>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/subscriptions/${id}`, data);
            const updatedSub = response.data;
            set((state) => ({
                subscriptions: state.subscriptions.map((o) => (o.id === id ? updatedSub : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update subscription', isLoading: false });
            throw error;
        }
    },
}));
